/**
 * Score badges (decision 0032): a public link and an image a business can show customers, suppliers and
 * insurers. A badge shows the business's name, its latest complete CyberScore, the band and how much of it
 * is verified. Nothing else leaves CyberScore: not the answers, the controls, the evidence or the insurer.
 * The business turns it on and off; turning it off makes the link stop working at once.
 */
export const BADGE_VERSION = 'badge/v1';
/** Links are 22 characters of base64url: 128 random bits, so they can't be guessed. */
export const BADGE_TOKEN = /^[A-Za-z0-9_-]{22}$/;
export const BAND_LABEL = {
    POOR: 'Poor',
    FAIR: 'Fair',
    GOOD: 'Good',
    VERY_GOOD: 'Very good',
    EXCELLENT: 'Excellent',
};
export const BAND_COLOUR = {
    POOR: '#DF1C41',
    FAIR: '#FD7521',
    GOOD: '#007EF9',
    VERY_GOOD: '#28A98F',
    EXCELLENT: '#1F8F78',
};
const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
/** At most `n` characters, with an ellipsis when cut. */
const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
/** "5 Oct 2026", in Malaysia's time zone, whatever the server's. */
export function badgeDate(iso) {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' }); // prettier-ignore
}
/**
 * The badge as an SVG image, for a website or an email signature. Self-contained: no scripts, no links, no
 * external fonts or images, and every text escaped, so it is safe to serve to anyone.
 */
export function badgeSvg(b) {
    const colour = BAND_COLOUR[b.band];
    const verified = Math.round(b.verified * 100);
    const name = esc(clip(b.business, 34));
    const label = `CyberScore ${b.score} of 1000, ${BAND_LABEL[b.band]}, ${verified}% verified, ${b.business}`;
    const font = "font-family=\"'DM Sans','Segoe UI',Helvetica,Arial,sans-serif\"";
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="96" viewBox="0 0 300 96" role="img" aria-label="${esc(label)}">`,
        `<title>${esc(label)}</title>`,
        '<rect x="0.5" y="0.5" width="299" height="95" rx="14" fill="#FFFFFF" stroke="#DFE1E7"/>',
        `<rect x="0.5" y="0.5" width="6" height="95" rx="3" fill="${colour}"/>`,
        '<rect x="18" y="18" width="36" height="36" rx="11" fill="#007EF9"/>',
        '<path d="M36 26l9 3.6v6.2c0 5.4-3.8 9.7-9 11.2-5.2-1.5-9-5.8-9-11.2v-6.2z" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>',
        '<path d="M32 37l3 3 5.5-6" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
        `<text x="66" y="27" ${font} font-size="10.5" font-weight="600" letter-spacing="0.8" fill="#818898">CYBERSCORE · ${verified}% VERIFIED</text>`,
        `<text x="66" y="55" ${font} font-size="28" font-weight="600" fill="#0D0D12">${b.score}</text>`,
        `<text x="${66 + String(b.score).length * 16.5 + 8}" y="54" ${font} font-size="12" font-weight="700" letter-spacing="1.4" fill="${colour}">${esc(BAND_LABEL[b.band].toUpperCase())}</text>`,
        `<text x="66" y="74" ${font} font-size="12" fill="#36394A">${name}</text>`,
        `<text x="66" y="88" ${font} font-size="10.5" fill="#818898">Checked ${esc(badgeDate(b.scored_at))}</text>`,
        '</svg>',
    ].join('');
}
//# sourceMappingURL=badges.js.map